/*
 * DelayLabel
 *
 * A component that checks and shows the delay label of a bank.
 * Used in the sender bank options or tables.
 */

import classNames from "classnames";
import { useTranslation } from "react-i18next";

import PopupTooltip, { PopupTooltipHoverProps } from "src/components-v2/PopupTooltip";

import { STATUS_DELAY } from "src/consts/delay";
import { useBankName } from "src/hooks/bank";
import { StyledDelayLabel } from "./index.styled";

type DelayLabelProps = {
  /**
   * Current bank status
   * @default {"1"} (NORMAL)
   */
  status?: string;

  /**
   * Current maintenance status
   * @default {false} (NORMAL)
   */
  isMaintenance?: boolean;

  /**
   * Current bank key
   * > Used only when tooltip value is given
   */
  bank?: string;

  /**
   * Tooltip position. Pass this value if a tooltip want to be shown
   */
  tooltip?: PopupTooltipHoverProps["position"];

  /**
   * Additional tooltip classnames
   * > Used only when tooltip value is given
   */
  tooltipClass?: string;

  /**
   * Classname to styling the "label" element
   */
  labelClass?: string;

  /**
   * identifier for bank or VA
   */
  isVA?: boolean | null;
};

const defaultProps = {
  status: STATUS_DELAY.NORMAL,
  isMaintenance: false,
  bank: "",
  tooltip: "",
  tooltipClass: ""
};

const DELAY_TYPES = {
  [STATUS_DELAY.NORMAL]: "normal",
  [STATUS_DELAY.MINOR]: "minor",
  [STATUS_DELAY.MAJOR]: "major"
};

const LABEL_COLORS = {
  [STATUS_DELAY.MINOR]: "banana",
  [STATUS_DELAY.MAJOR]: "sakura"
} as const;

function DelayLabel(props: DelayLabelProps) {
  const { status, isMaintenance, bank, tooltip, labelClass, tooltipClass, isVA } = props;
  const { t } = useTranslation("translation");
  const { getBankName } = useBankName();

  if (status === STATUS_DELAY.NORMAL && !isMaintenance) return null;

  const delayType = DELAY_TYPES[status];

  if (tooltip) {
    return (
      <PopupTooltip.Hover
        position={tooltip}
        tooltipClassName={classNames("u-w-56", tooltipClass)}
        content={
          <>
            {t(`bank-delay.info-${delayType}.tooltip`, { bank: getBankName(bank, isVA) })}
            <b>{t(`bank-delay.info-${delayType}.tooltip-bold`)}</b>
            {t(`bank-delay.info-${delayType}.tooltip-cont`)}
          </>
        }
      >
        <StyledDelayLabel
          color={LABEL_COLORS[status]}
          isMaintenance={isMaintenance}
          className={classNames("u-ml-2 u-px-2 u-py-1", labelClass)}
        >
          {isMaintenance ? t("bank-delay.label.maintenance") : t(`bank-delay.label.${delayType}`)}
        </StyledDelayLabel>
      </PopupTooltip.Hover>
    );
  }

  return (
    <StyledDelayLabel
      color={LABEL_COLORS[status]}
      isMaintenance={isMaintenance}
      className={classNames("u-ml-2 u-px-2 u-py-1", labelClass)}
    >
      {isMaintenance ? t("bank-delay.label.maintenance") : t(`bank-delay.label.${delayType}`)}
    </StyledDelayLabel>
  );
}

DelayLabel.defaultProps = defaultProps;

export default DelayLabel;
